import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { hasConsent } from './CookieConsent';

interface ConversionTrackingProps {
  pageName: string;
  conversionLabel?: string;
}

export const ConversionTracking = ({ pageName, conversionLabel }: ConversionTrackingProps) => {
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID;
  const facebookPixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;

  // Check if user has consented to marketing cookies
  const hasMarketingConsent = hasConsent('marketing');

  useEffect(() => {
    // Only track if user has consented
    if (!hasMarketingConsent) {
      console.log('Tracking blocked: No marketing consent');
      return;
    }

    // Track page view in Facebook Pixel
    if (window.fbq && facebookPixelId) {
      window.fbq('track', 'PageView');
      console.log('Facebook Pixel: PageView tracked for', pageName);
    }

    // Track page view in Google Ads
    if (window.gtag && googleAdsId) {
      window.gtag('event', 'page_view', {
        page_title: pageName,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
      console.log('Google Ads: PageView tracked for', pageName);
    }
  }, [pageName, googleAdsId, facebookPixelId, hasMarketingConsent]);

  // Only load tracking scripts if user has consented
  if (!hasMarketingConsent) {
    return null;
  }

  return (
    <Helmet>
      {/* Google Ads Global Site Tag */}
      {googleAdsId && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`} />
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${googleAdsId}', {
                'anonymize_ip': true,
                'allow_ad_personalization_signals': false
              });
            `}
          </script>
        </>
      )}

      {/* Facebook Pixel */}
      {facebookPixelId && (
        <script>
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${facebookPixelId}');
          `}
        </script>
      )}
      <noscript>
        {facebookPixelId && (
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            src={`https://www.facebook.com/tr?id=${facebookPixelId}&ev=PageView&noscript=1`}
            alt=""
          />
        )}
      </noscript>
    </Helmet>
  );
};

// Track custom conversion events
export const trackConversion = (eventName: string, params?: Record<string, any>) => {
  // Check if user has consented to marketing cookies
  if (!hasConsent('marketing')) {
    console.log('Conversion tracking blocked: No marketing consent');
    return;
  }

  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID;
  const facebookPixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;

  // Facebook Pixel event
  if (window.fbq && facebookPixelId) {
    window.fbq('track', eventName, params);
    console.log('Facebook Pixel: Event tracked -', eventName, params);
  }

  // Google Ads conversion event
  if (window.gtag && googleAdsId) {
    window.gtag('event', 'conversion', {
      send_to: `${googleAdsId}/${params?.label || ''}`,
      value: params?.value || 0,
      currency: 'USD',
      ...params,
    });
    console.log('Google Ads: Conversion tracked -', eventName, params);
  }
};

// Declare global types for tracking functions
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}
